import OverlayBase from "./OverlayBase";
import {LimitErrorResponse} from "../../../util/fetch";
import {DEFAULT_FEATURE_COPY, FEATURE_COPY, getPlanByProductId} from "../../../pricing";
import {ArrowRight} from "lucide-react";

export default function SubscriptionLimitOverlay(props: { error: LimitErrorResponse, hide: () => void }) {
    const {error} = props
    const copy = FEATURE_COPY[error.limitName] ?? DEFAULT_FEATURE_COPY
    const Icon = copy.Icon

    const currentPlanName = getPlanByProductId(error.currentProductId)?.name ?? "Free"
    const nextPlan = getPlanByProductId(error.nextProductId)

    const onUpgrade = () => {
        props.hide()
        window.location.assign("/pricing")
    }

    return (
        <OverlayBase hide={props.hide} w="w-[32rem]" noPadding>
            <div className="h-28 rounded-t-lg bg-gradient-to-br from-fuchsia-500 via-primary to-cyan-400 flex items-center justify-center">
                <div className="bg-neutral-900/30 backdrop-blur-sm rounded-full p-3 ring-1 ring-white/30">
                    <Icon className="size-8 text-white"/>
                </div>
            </div>

            <div className="p-6">
                <h2 className="text-2xl font-bold text-white leading-tight mb-3">
                    {copy.title}
                </h2>
                <p className="text-neutral-300 text-sm leading-relaxed mb-6">
                    {copy.description(error.limitValue, currentPlanName)}
                </p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                    <PlanCard
                        label="Current plan"
                        name={currentPlanName}
                        value={copy.formatValue(error.limitValue)}
                        valueLabel={copy.valueLabel}
                    />
                    <PlanCard
                        label="Recommended"
                        name={nextPlan?.name ?? "—"}
                        value={copy.formatValue(error.nextLimitValue)}
                        valueLabel={copy.valueLabel}
                        highlight
                    />
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                    <button onClick={props.hide}
                            className="px-4 py-2 text-neutral-300 hover:text-white rounded-lg">
                        Maybe later
                    </button>
                    <button onClick={onUpgrade}
                            disabled={!nextPlan}
                            className="px-5 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-semibold flex items-center justify-center gap-2 disabled:bg-neutral-600 disabled:cursor-not-allowed">
                        Upgrade now
                        <ArrowRight className="size-4"/>
                    </button>
                </div>
            </div>
        </OverlayBase>
    )
}

function PlanCard(props: {
    label: string,
    name: string,
    value: string,
    valueLabel: string,
    highlight?: boolean,
}) {
    const baseClasses = "rounded-lg p-3 border flex flex-col justify-between min-h-[5.5rem]"
    const themeClasses = props.highlight
        ? "border-primary bg-primary/10"
        : "border-neutral-700 bg-neutral-800/50"

    return (
        <div className={`${baseClasses} ${themeClasses}`}>
            <div className="text-xs uppercase tracking-wider text-neutral-400">
                {props.label}
            </div>
            <div className="flex items-end justify-between mt-2 gap-2">
                <div className="text-lg font-semibold text-white truncate">
                    {props.name}
                </div>
                <div className="text-right shrink-0">
                    <div className="text-2xl font-bold text-white leading-none">
                        {props.value}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-400 mt-1">
                        {props.valueLabel}
                    </div>
                </div>
            </div>
        </div>
    )
}
